import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { useNotify, useTranslate } from "react-admin";

import { NoteGenerationStatus, NoteKind } from "../generated/graphql";
import { Activity } from "../lib/activityUtils";
import { color } from "../theme/designTokens";
import { dialogContentSx, dialogPaperSx, dialogTitleSx } from "../theme/dialogStyles";
import { NoteType } from "../types/note";
import { DocumentationSyncStatus, visibleDocumentationSyncs } from "./fields/DocumentationSyncStatus";

type SessionDocumentationDialogProps = {
  activity: Activity;
  /** On a resident's page, only that resident's export is shown. */
  seniorId?: string;
  onClose: () => void;
};

/** The three session note kinds, in the same order the senior app shows them. */
const DOC_KINDS: { kind: NoteKind; labelKey: string; emptyKey: string }[] = [
  {
    kind: NoteKind.AiSummary,
    labelKey: "sessionDocumentation.aiSummary",
    emptyKey: "sessionDocumentation.emptyAiSummary",
  },
  { kind: NoteKind.Note, labelKey: "sessionDocumentation.note", emptyKey: "sessionDocumentation.emptyNote" },
  { kind: NoteKind.AiNote, labelKey: "sessionDocumentation.aiNote", emptyKey: "sessionDocumentation.emptyAiNote" },
];

/**
 * Read-only documentation for one activity session, mirroring the senior app's
 * "Dokumentation zur Aktivität": the AI summary, manual note and behavioural note
 * as titled blocks, each copyable, plus a combined preview that copies all three.
 * The AI summary shows a being-generated / failed state from its generation status.
 */
export const SessionDocumentationDialog = ({ activity, seniorId, onClose }: SessionDocumentationDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();

  const notes = activity.notes ?? [];
  const noteByKind = (kind: NoteKind): NoteType | undefined => notes.find(n => n.kind === kind);

  const copy = (text: string): void => {
    void navigator.clipboard.writeText(text);
    notify("sessionDocumentation.copySuccess", { type: "success" });
  };

  // Combined preview: the note bodies in display order, blank-line separated.
  // A caregiver has to confirm the documentation before it reaches the care
  // system; until then the manager sees it, marked as not yet filed.
  const isDocumented = notes.some(note => !!note.publishedAt);

  const documentationSyncs = visibleDocumentationSyncs(
    notes.filter(note => note.kind === NoteKind.ActivityDocumentation).flatMap(note => note.documentationSyncs ?? []),
    seniorId
  );
  const residentName = (participantId?: string | null): string | undefined =>
    activity.participants?.find(participant => participant.id === participantId)?.name ?? undefined;

  const documentation = DOC_KINDS.map(({ kind }) => noteByKind(kind)?.note)
    .filter((value): value is string => !!value)
    .join("\n\n");

  const copyButton = (text: string, labelKey: string): ReactNode => (
    <IconButton size="small" onClick={() => copy(text)} aria-label={translate(labelKey)}>
      <ContentCopyIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle
        sx={{
          ...dialogTitleSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {translate("sessionDocumentation.title")}
          <Chip
            size="small"
            color={isDocumented ? "default" : "warning"}
            label={translate(isDocumented ? "sessionDocumentation.documented" : "sessionDocumentation.notDocumented")}
          />
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={translate("ra.action.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          ...dialogContentSx,
          display: "grid",
          gap: 3,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {DOC_KINDS.map(({ kind, labelKey, emptyKey }) => {
          const note = noteByKind(kind);
          const isGenerating = kind === NoteKind.AiSummary && note?.generationStatus === NoteGenerationStatus.Pending;
          const isFailed = kind === NoteKind.AiSummary && note?.generationStatus === NoteGenerationStatus.Failed;
          const text = note?.note ?? "";

          return (
            <Box key={kind} sx={{ display: "grid", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 32 }}>
                <Typography variant="caption" color={color.gray}>
                  {translate(labelKey)}
                </Typography>
                {!!text && !isGenerating && copyButton(text, "sessionDocumentation.copy")}
              </Box>
              {isGenerating ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {translate("sessionDocumentation.generating")}
                  </Typography>
                </Box>
              ) : isFailed ? (
                <Typography variant="body2" color="error">
                  {translate("sessionDocumentation.failed")}
                </Typography>
              ) : (
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  color={text ? "text.primary" : "text.secondary"}
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {text || translate(emptyKey)}
                </Typography>
              )}
            </Box>
          );
        })}

        <Divider />

        <Box sx={{ display: "grid", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 32 }}>
            <Typography variant="caption" color={color.gray}>
              {translate("sessionDocumentation.preview")}
            </Typography>
            {!!documentation && copyButton(documentation, "sessionDocumentation.copyAll")}
          </Box>
          <Typography
            variant="body2"
            color={documentation ? "text.primary" : "text.secondary"}
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {documentation || translate("sessionDocumentation.empty")}
          </Typography>
        </Box>

        {documentationSyncs.length > 0 && (
          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="caption" color={color.gray}>
              {translate("sessionDocumentation.syncTitle")}
            </Typography>
            <DocumentationSyncStatus syncs={documentationSyncs} getLabel={sync => residentName(sync.seniorId)} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
